import { TextInput, Stack, Switch, Select, Group, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import { InsuranceProvider } from "../../../services/admin/insuranceProviders.service";
import { useStates } from "../../../hooks/useReferenceData";

export interface InsuranceProviderFormValues {
  name: string;
  naicNumber: string;
  mailingAddress: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  isActive: boolean;
}

interface InsuranceProviderFormProps {
  initialValues?: InsuranceProvider;
  existingProviders?: InsuranceProvider[];
  onSubmit: (values: InsuranceProviderFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function InsuranceProviderForm({
  initialValues,
  existingProviders,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
}: InsuranceProviderFormProps) {
  const { data: states = [] } = useStates();

  const form = useForm<InsuranceProviderFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      naicNumber: initialValues?.naicNumber || "",
      mailingAddress: initialValues?.mailingAddress || "",
      city: initialValues?.city || "",
      state: initialValues?.state || "",
      zipCode: initialValues?.zipCode || "",
      phoneNumber: initialValues?.phoneNumber || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      // Provider name: required, min 2 chars, check duplicates
      name: (value) => {
        const trimmed = value?.trim();
        if (!trimmed) return "Provider name is required";
        if (trimmed.length < 2)
          return "Provider name must be at least 2 characters";

        // Check duplicates (excluding current provider in edit mode)
        const isDuplicate = existingProviders?.some(
          (p) =>
            p.name.toLowerCase() === trimmed.toLowerCase() &&
            p.id !== initialValues?.id
        );
        if (isDuplicate) return "A provider with this name already exists";

        return null;
      },

      // ZIP Code: must be 5 digits or 5+4 format (if provided)
      zipCode: (value) => {
        if (value && !/^\d{5}(-\d{4})?$/.test(value)) {
          return "ZIP code must be 5 digits (e.g., 79998) or 9 digits (e.g., 79998-1234)";
        }
        return null;
      },

      // Phone: must be 10 digits (if provided)
      phoneNumber: (value) => {
        if (value) {
          const digitsOnly = value.replace(/\D/g, "");
          if (digitsOnly.length !== 10) {
            return "Phone number must contain 10 digits";
          }
        }
        return null;
      },

      // NAIC: must be 5 digits (if provided)
      naicNumber: (value) => {
        if (value && !/^\d{5}$/.test(value)) {
          return "NAIC number must be 5 digits";
        }
        return null;
      },
    },
  });

  const handleValidationError = (errors: typeof form.errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(
        `[data-field="${firstErrorField}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        if (errorElement instanceof HTMLElement) {
          setTimeout(() => errorElement.focus(), 300);
        }
      }
    }
  };

  return (
    <form noValidate onSubmit={form.onSubmit(onSubmit, handleValidationError)}>
      <Stack gap="md">
        <TextInput
          label="Insurance Provider Name"
          placeholder="e.g., Blue Cross Blue Shield, Aetna, Medicare"
          required
          data-field="name"
          {...form.getInputProps("name")}
        />

        <TextInput
          label="NAIC Number"
          placeholder="Enter NAIC number (optional)"
          description="National Association of Insurance Commissioners number (5 digits)"
          data-field="naicNumber"
          {...form.getInputProps("naicNumber")}
        />

        <TextInput
          label="Mailing Address"
          placeholder="e.g., PO BOX 981106"
          description="Primary mailing address (often PO Box)"
          {...form.getInputProps("mailingAddress")}
        />

        <TextInput
          label="City"
          placeholder="e.g., El Paso"
          {...form.getInputProps("city")}
        />

        <Select
          label="State"
          placeholder="Select state"
          description="Two-letter state abbreviation"
          data={states}
          searchable
          clearable
          data-field="state"
          {...form.getInputProps("state")}
        />

        <TextInput
          label="ZIP Code"
          placeholder="e.g., 79998 or 79998-1234"
          description="5 or 9 digit ZIP code"
          data-field="zipCode"
          {...form.getInputProps("zipCode")}
        />

        <TextInput
          label="Phone Number"
          placeholder="e.g., 800-338-8047"
          description="Primary contact/claims phone number (10 digits)"
          data-field="phoneNumber"
          {...form.getInputProps("phoneNumber")}
        />

        <Switch
          label="Active"
          description="Inactive providers will not appear in dropdowns"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />

        {(onCancel || submitLabel !== undefined) && (
          <Group justify="flex-end" gap="xs" mt="md">
            {onCancel && (
              <Button variant="default" onClick={onCancel} type="button">
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isSubmitting}>
              {submitLabel || (initialValues ? "Update" : "Create")}
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
